import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { admin } from '../../lib/api';
import type { Season, Tournament } from '../../types';
import { Button } from '../../ui/button';
import { ErrorMessage, Field, Label } from '../../ui/fieldset';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Text } from '../../ui/text';
import { EmptyState, Panel, StatCard } from '../../components/PageChrome';

const fmtDate = (iso: string) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

interface SeasonFormState {
  name: string;
  startDate: string;
  endDate: string;
  notes: string;
}

interface TournamentFormState {
  name: string;
  startDate: string;
  endDate: string;
  venue: string;
  seasonId: string;
  notes: string;
}

const emptySeasonForm = (): SeasonFormState => ({
  name: '',
  startDate: '',
  endDate: '',
  notes: '',
});

const emptyTournamentForm = (): TournamentFormState => ({
  name: '',
  startDate: '',
  endDate: '',
  venue: '',
  seasonId: '',
  notes: '',
});

export default function Schedule() {
  const { teamId } = useParams<{ teamId: string }>();

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [error, setError] = useState<string>();

  const [showNewSeason, setShowNewSeason] = useState(false);
  const [showNewTournament, setShowNewTournament] = useState(false);
  const [editingSeasonId, setEditingSeasonId] = useState<string>();
  const [editingTournamentId, setEditingTournamentId] = useState<string>();

  const load = async () => {
    if (!teamId) return;
    try {
      const [s, t] = await Promise.all([
        admin.listSeasons(teamId),
        admin.listTournaments(teamId),
      ]);
      setSeasons([...s.seasons].sort((a, b) => b.startDate.localeCompare(a.startDate)));
      setTournaments(
        [...t.tournaments].sort((a, b) => b.startDate.localeCompare(a.startDate)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading level={2}>Schedule structure</Heading>
          <Text className="mt-1 text-sm">
            Organize seasons and tournament weekends so games stay easy to group and find.
          </Text>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Seasons" value={seasons.length} />
        <StatCard label="Tournaments" value={tournaments.length} />
        <StatCard
          label="Active seasons"
          value={seasons.filter((s) => s.active).length}
          tone="emerald"
        />
      </div>

      {error ? <Text className="text-red-400">{error}</Text> : null}

      <section className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Heading level={3}>Seasons</Heading>
            <Text className="mt-1 text-sm">
              Date ranges for spring, summer, fall ball, or any other season.
            </Text>
          </div>
          <Button
            color="dark/zinc"
            onClick={() => {
              setShowNewSeason((v) => !v);
              setEditingSeasonId(undefined);
            }}
          >
            {showNewSeason ? 'Cancel' : 'Add season'}
          </Button>
        </div>

        {showNewSeason ? (
          <Panel>
            <SeasonForm
              initial={emptySeasonForm()}
              onSubmit={async (form) => {
                if (!teamId) return;
                await admin.createSeason(teamId, form);
                setShowNewSeason(false);
                await load();
              }}
              onCancel={() => setShowNewSeason(false)}
              submitLabel="Add season"
            />
          </Panel>
        ) : null}

        {seasons.length === 0 ? (
          <EmptyState
            title="No seasons yet"
            body="A season groups your games, like 2026 Spring or Fall Ball."
            action={<Button color="dark/zinc" onClick={() => setShowNewSeason(true)}>Add season</Button>}
          />
        ) : (
          <ul className="grid gap-3 lg:grid-cols-2">
            {seasons.map((s) =>
              editingSeasonId === s.seasonId ? (
                <li key={s.seasonId}>
                  <Panel>
                    <SeasonForm
                      initial={{
                        name: s.name,
                        startDate: s.startDate,
                        endDate: s.endDate,
                        notes: s.notes ?? '',
                      }}
                      onSubmit={async (form) => {
                        if (!teamId) return;
                        await admin.updateSeason(teamId, s.seasonId, {
                          name: form.name,
                          startDate: form.startDate,
                          endDate: form.endDate,
                          notes: form.notes || null,
                        });
                        setEditingSeasonId(undefined);
                        await load();
                      }}
                      onCancel={() => setEditingSeasonId(undefined)}
                      submitLabel="Save season"
                    />
                  </Panel>
                </li>
              ) : (
                <li key={s.seasonId} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="font-medium text-zinc-100">{s.name}</div>
                      <div className="mt-1 text-sm text-zinc-400">
                        {fmtDate(s.startDate)} to {fmtDate(s.endDate)}
                      </div>
                      {s.notes ? <Text className="mt-2 text-sm">{s.notes}</Text> : null}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button
                        plain
                        onClick={() => {
                          setEditingSeasonId(s.seasonId);
                          setShowNewSeason(false);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        plain
                        className="text-red-400"
                        onClick={async () => {
                          if (!teamId) return;
                          if (!window.confirm(`Delete season "${s.name}"?`)) return;
                          try {
                            await admin.deleteSeason(teamId, s.seasonId);
                            await load();
                          } catch (err) {
                            setError(err instanceof Error ? err.message : String(err));
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </li>
              ),
            )}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Heading level={3}>Tournaments</Heading>
            <Text className="mt-1 text-sm">
              Multi-game events with venue, season, and note context.
            </Text>
          </div>
          <Button
            color="dark/zinc"
            onClick={() => {
              setShowNewTournament((v) => !v);
              setEditingTournamentId(undefined);
            }}
          >
            {showNewTournament ? 'Cancel' : 'Add tournament'}
          </Button>
        </div>

        {showNewTournament ? (
          <Panel>
            <TournamentForm
              initial={emptyTournamentForm()}
              seasons={seasons}
              onSubmit={async (form) => {
                if (!teamId) return;
                await admin.createTournament(teamId, {
                  name: form.name,
                  startDate: form.startDate,
                  endDate: form.endDate,
                  venue: form.venue || undefined,
                  seasonId: form.seasonId || undefined,
                  notes: form.notes || undefined,
                });
                setShowNewTournament(false);
                await load();
              }}
              onCancel={() => setShowNewTournament(false)}
              submitLabel="Add tournament"
            />
          </Panel>
        ) : null}

        {tournaments.length === 0 ? (
          <EmptyState
            title="No tournaments yet"
            body="Add brackets or weekend events here, then associate tournament games when scheduling."
            action={<Button color="dark/zinc" onClick={() => setShowNewTournament(true)}>Add tournament</Button>}
          />
        ) : (
          <ul className="grid gap-3 lg:grid-cols-2">
            {tournaments.map((t) =>
              editingTournamentId === t.tournamentId ? (
                <li key={t.tournamentId}>
                  <Panel>
                    <TournamentForm
                      initial={{
                        name: t.name,
                        startDate: t.startDate,
                        endDate: t.endDate,
                        venue: t.venue ?? '',
                        seasonId: t.seasonId ?? '',
                        notes: t.notes ?? '',
                      }}
                      seasons={seasons}
                      onSubmit={async (form) => {
                        if (!teamId) return;
                        await admin.updateTournament(teamId, t.tournamentId, {
                          name: form.name,
                          startDate: form.startDate,
                          endDate: form.endDate,
                          venue: form.venue || null,
                          seasonId: form.seasonId || null,
                          notes: form.notes || null,
                        });
                        setEditingTournamentId(undefined);
                        await load();
                      }}
                      onCancel={() => setEditingTournamentId(undefined)}
                      submitLabel="Save tournament"
                    />
                  </Panel>
                </li>
              ) : (
                <li key={t.tournamentId} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="font-medium text-zinc-100">{t.name}</div>
                      <div className="mt-1 text-sm text-zinc-400">
                        {fmtDate(t.startDate)} to {fmtDate(t.endDate)}
                        {t.venue ? ` · ${t.venue}` : ''}
                      </div>
                      {t.seasonId ? (
                        <div className="mt-1 text-xs text-zinc-500">
                          Season: {seasons.find((s) => s.seasonId === t.seasonId)?.name ?? t.seasonId}
                        </div>
                      ) : null}
                      {t.notes ? <Text className="mt-2 text-sm">{t.notes}</Text> : null}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button
                        plain
                        onClick={() => {
                          setEditingTournamentId(t.tournamentId);
                          setShowNewTournament(false);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        plain
                        className="text-red-400"
                        onClick={async () => {
                          if (!teamId) return;
                          if (!window.confirm(`Delete tournament "${t.name}"?`)) return;
                          try {
                            await admin.deleteTournament(teamId, t.tournamentId);
                            await load();
                          } catch (err) {
                            setError(err instanceof Error ? err.message : String(err));
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </li>
              ),
            )}
          </ul>
        )}
      </section>
    </div>
  );
}

function SeasonForm({
  initial,
  onSubmit,
  onCancel,
  submitLabel,
}: {
  initial: SeasonFormState;
  onSubmit: (f: SeasonFormState) => Promise<void>;
  onCancel: () => void;
  submitLabel: string;
}) {
  const [form, setForm] = useState<SeasonFormState>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await onSubmit({
        ...form,
        name: form.name.trim(),
        notes: form.notes.trim(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field>
        <Label>Season name</Label>
        <Input
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder='e.g., "2026 Spring"'
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field>
          <Label>Start date</Label>
          <Input
            required
            type="date"
            value={form.startDate}
            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
          />
        </Field>
        <Field>
          <Label>End date</Label>
          <Input
            required
            type="date"
            value={form.endDate}
            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
          />
        </Field>
      </div>
      <Field>
        <Label>Notes</Label>
        <Textarea
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </Field>
      {error ? <ErrorMessage>{error}</ErrorMessage> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" color="dark/zinc" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" outline onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function TournamentForm({
  initial,
  seasons,
  onSubmit,
  onCancel,
  submitLabel,
}: {
  initial: TournamentFormState;
  seasons: Season[];
  onSubmit: (f: TournamentFormState) => Promise<void>;
  onCancel: () => void;
  submitLabel: string;
}) {
  const [form, setForm] = useState<TournamentFormState>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await onSubmit({
        ...form,
        name: form.name.trim(),
        venue: form.venue.trim(),
        notes: form.notes.trim(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field>
        <Label>Tournament name</Label>
        <Input
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder='e.g., "Memorial Day Classic"'
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field>
          <Label>Start date</Label>
          <Input
            required
            type="date"
            value={form.startDate}
            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
          />
        </Field>
        <Field>
          <Label>End date</Label>
          <Input
            required
            type="date"
            value={form.endDate}
            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
          />
        </Field>
      </div>
      <Field>
        <Label>Venue</Label>
        <Input
          value={form.venue}
          onChange={(e) => setForm({ ...form, venue: e.target.value })}
          placeholder="Optional"
        />
      </Field>
      <Field>
        <Label>Season</Label>
        <Select
          value={form.seasonId}
          onChange={(e) => setForm({ ...form, seasonId: e.target.value })}
        >
          <option value="">— No associated season —</option>
          {seasons.map((s) => (
            <option key={s.seasonId} value={s.seasonId}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field>
        <Label>Notes</Label>
        <Textarea
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </Field>
      {error ? <ErrorMessage>{error}</ErrorMessage> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" color="dark/zinc" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" outline onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
